import { Fragment, type ReactNode } from 'react';

/**
 * A small Markdown renderer for help-centre articles and landing-page text.
 * It builds React elements — never HTML strings — so nothing a writer types
 * can run as script. Links are limited to http(s), mailto and tel.
 *
 * Supports: # headings, paragraphs, **bold**, *italic*, `code`, [links](url),
 * ![images](url), - and 1. lists, > quotes, ``` code blocks ``` and ---.
 */

const safeUrl = (url: string) => (/^(https?:|mailto:|tel:|\/)/i.test(url.trim()) ? url.trim() : '#');

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(!\[([^\]]*)\]\(([^)\s]+)\))|(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(_([^_]+)_)|(`([^`]+)`)/g;
  let last = 0;
  let index = 0;
  for (const match of text.matchAll(pattern)) {
    if ((match.index ?? 0) > last) out.push(text.slice(last, match.index));
    const key = `${keyBase}-${index++}`;
    if (match[1]) out.push(<img key={key} src={safeUrl(match[3])} alt={match[2]} className="my-4 max-w-full rounded-xl" loading="lazy" />);
    else if (match[4]) {
      const href = safeUrl(match[6]);
      const external = /^https?:/i.test(href);
      out.push(
        <a key={key} href={href} className="font-medium text-primary underline underline-offset-2" {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}>
          {match[5]}
        </a>,
      );
    } else if (match[7]) out.push(<strong key={key}>{match[8]}</strong>);
    else if (match[9]) out.push(<em key={key}>{match[10]}</em>);
    else if (match[11]) out.push(<em key={key}>{match[12]}</em>);
    else if (match[13]) out.push(<code key={key} className="rounded bg-muted px-1.5 py-0.5 text-[0.9em]">{match[14]}</code>);
    last = (match.index ?? 0) + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index];
    const k = `b${key++}`;

    if (!line.trim()) {
      index += 1;
      continue;
    }
    if (line.startsWith('```')) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith('```')) code.push(lines[index++]);
      index += 1;
      blocks.push(
        <pre key={k} className="my-4 overflow-x-auto rounded-xl bg-slate-900 p-4 text-sm text-slate-100">
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const content = inline(heading[2], k);
      const styles = ['mt-8 mb-3 text-2xl font-extrabold', 'mt-7 mb-3 text-xl font-bold', 'mt-6 mb-2 text-lg font-bold', 'mt-5 mb-2 font-bold'];
      const Tag = (['h2', 'h3', 'h4', 'h5'] as const)[level - 1];
      blocks.push(
        <Tag key={k} className={styles[level - 1]}>
          {content}
        </Tag>,
      );
      index += 1;
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      blocks.push(<hr key={k} className="my-6" />);
      index += 1;
      continue;
    }
    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (index < lines.length && lines[index].startsWith('>')) quote.push(lines[index++].replace(/^>\s?/, ''));
      blocks.push(
        <blockquote key={k} className="my-4 border-s-4 border-primary/40 bg-primary/5 py-2 ps-4 pe-3 italic">
          {inline(quote.join(' '), k)}
        </blockquote>,
      );
      continue;
    }
    if (/^\s*([-*])\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (index < lines.length && (ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/).test(lines[index])) {
        items.push(lines[index++].replace(/^\s*(?:[-*]|\d+[.)])\s+/, ''));
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List key={k} className={`my-4 space-y-1.5 ps-6 ${ordered ? 'list-decimal' : 'list-disc'}`}>
          {items.map((item, at) => (
            <li key={at}>{inline(item, `${k}-${at}`)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const paragraph: string[] = [];
    while (index < lines.length && lines[index].trim() && !/^(#{1,4}\s|```|>|\s*[-*]\s+|\s*\d+[.)]\s+)/.test(lines[index])) paragraph.push(lines[index++]);
    if (!paragraph.length) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(
      <p key={k} className="my-3 leading-relaxed">
        {paragraph.map((part, at) => (
          <Fragment key={at}>
            {at > 0 && <br />}
            {inline(part, `${k}-${at}`)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return <div className={className}>{blocks}</div>;
}
